import { useQueryClient } from '@tanstack/react-query';
import { array, record, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useCallback } from 'react';
import { getRepositoryQueryKey } from './getRepositoryQueryKey';

export type InvalidateQueries<RepositoryRegistry> = {
    [RepositoryName in keyof RepositoryRegistry]?: Array<keyof RepositoryRegistry[RepositoryName]>;
};

type RepositoryMethodTuple<
    RepositoryRegistry,
    RepositoryName extends keyof RepositoryRegistry = keyof RepositoryRegistry,
> = [repositoryName: RepositoryName, methodName: keyof RepositoryRegistry[RepositoryName]];

export const useInvalidateRepositoryQueries = <RepositoryRegistry>() => {
    const queryClient = useQueryClient();

    return useCallback(
        (queries: InvalidateQueries<RepositoryRegistry>) =>
            pipe(
                queries,
                record.collect(string.Ord)(
                    (repo, methods) =>
                        methods?.map(
                            (method) =>
                                [repo, method] as RepositoryMethodTuple<RepositoryRegistry, keyof RepositoryRegistry>
                        ) ?? []
                ),
                array.flatten,
                array.map(async ([repo, method]) =>
                    queryClient.invalidateQueries({
                        queryKey: getRepositoryQueryKey<RepositoryRegistry, typeof repo, typeof method, never>(
                            repo,
                            method
                        ),
                    })
                )
            ),
        [queryClient]
    );
};
